import { FONT_FAMILIES } from '@/constant';
import { capture } from '@/handlers/error';
import { useOutsideClick } from '@/hooks/useOutsideClick';
import { ResizeBorder } from '@/pages/LessonEditor/components/ResizeBorder';
import { useComponentEditor, useSlideEditor } from '@/providers/editor/EditorProvider';
import { Coordinate, isSlideComponentMC, PropsWithPreview, Question, Size, SlideComponent, SlideComponentMCAnswer } from '@/types/slide';
import AddBoxOutlinedIcon from '@mui/icons-material/AddBoxOutlined';
import DeleteIcon from '@mui/icons-material/Delete';
import { Box } from '@mui/material';
import { useNotifications } from '@toolpad/core';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDebounce } from 'rooks';
import { ComponentLabel } from '../ComponentLabel';
import { ContextualBar } from '../ContextualBar';
import { useSlideComponent } from '../hooks/useSlideComponent';
import { SlideRef } from '../Slide';
import { SlideComponentMCAnswerObject } from './SlideComponentMCAnswerObject';

interface Props {
    ref: React.RefObject<HTMLDivElement | null>;
    init: {
        model: SlideComponent;
        focus?: boolean;
    };
    slideRef: React.RefObject<SlideRef | null>;
    question?: Question;
}

export const SlideComponentMC: React.FC<PropsWithPreview<Props>> = ({ ref, init, preview, slideRef, question }) => {
    const { t } = useTranslation();
    const notifications = useNotifications();
    const { selectedSlide } = useSlideEditor();
    const { deleteComponent, updateComponent, createSlideImage } = useComponentEditor();

    const { isSelected } = useSlideComponent(ref, [], !!preview);

    const [model, setModel] = useState<SlideComponent>({ ...init.model });
    const [isFocus, setIsFocus] = useState<boolean>(init.focus ?? false);

    const [tSize, setTSize] = useState<Size>(model.size);
    const [tPos, setTPos] = useState<Coordinate>(model.pos);

    const isEditing = isFocus || isSelected;

    const onDeleteComponent = React.useCallback(
        async (id: string) => {
            try {
                await deleteComponent(id);
            } catch (error) {
                notifications.show(error instanceof Error ? error.message : `${error}`, { severity: 'error' });
                capture(error);
            }
        },
        [deleteComponent, notifications],
    );

    const onUpdateComponent = React.useCallback(
        async (id: string, component: SlideComponent) => {
            try {
                await updateComponent(id, component);
            } catch (error) {
                notifications.show(error instanceof Error ? error.message : `${error}`, { severity: 'error' });
                capture(error);
            }
        },
        [updateComponent, notifications],
    );

    const onUploadImage = React.useCallback(
        async (file: File): Promise<string> => {
            try {
                return await createSlideImage(selectedSlide.editorData?.id ?? '', file);
            } catch (error) {
                notifications.show(error instanceof Error ? error.message : `${error}`, { severity: 'error' });
                capture(error);
                return '';
            }
        },
        [createSlideImage, notifications, selectedSlide.editorData?.id],
    );

    const debouncedUpdateComponent = useDebounce(onUpdateComponent, 500);

    useOutsideClick(ref, () => {
        if (preview) return;
        if (!ref) return;

        setIsFocus(false);
    });

    if (!isSlideComponentMC(model)) {
        return null;
    }

    const onCreateAnswerObject = (component: SlideComponentMCAnswer) => {
        const updatedContent = { ...model.content, answers: [...model.content.answers, component] };
        const updateModel = { ...model, content: updatedContent };
        setModel({ ...updateModel });
        onUpdateComponent?.(model.id, { ...updateModel });
    };

    const onUpdateAnswerObject = (id: string, component: SlideComponentMCAnswer) => {
        const updatedContent = { ...model.content, answers: model.content.answers.map((zone) => (zone.id === id ? component : zone)) };
        const updateModel = { ...model, content: updatedContent };

        setModel({ ...updateModel });
        onUpdateComponent?.(model.id, { ...updateModel });
    };

    // allow only one answer with isAnswer == true
    const onCheckAnswerObject = (id: string) => {
        const updatedAnswers = model.content.answers.map((answer) => ({
            ...answer,
            settings: {
                ...answer.settings,
                isAnswer: answer.id === id,
            },
        }));

        const updatedContent = { ...model.content, answers: updatedAnswers };
        const updateModel = { ...model, content: updatedContent };

        setModel({ ...updateModel });
        onUpdateComponent?.(model.id, { ...updateModel });
    };

    const onDeleteAnswerObject = (id: string) => {
        if (model.content.answers.length <= 2) {
            notifications.show(t('slide.components.mc.message.error.answerObjectLessThanTwo'), { severity: 'error', autoHideDuration: 5000 });
            return;
        }
        const updatedContent = { ...model.content, answers: model.content.answers.filter((zone) => zone.id !== id) };
        const updateModel = { ...model, content: updatedContent };
        setModel({ ...updateModel });
        onUpdateComponent?.(model.id, { ...updateModel });
    };

    const onBringToFrontAnswerObject = (id: string) => {
        const itemToMove = model.content.answers.find((item) => item.id === id);
        const arrayWithoutItem = model.content.answers.filter((item) => item.id !== id);

        const updatedContent = { ...model.content, answers: itemToMove ? [...arrayWithoutItem, itemToMove] : arrayWithoutItem };
        const updateModel = { ...model, content: updatedContent };
        setModel({ ...updateModel });
        onUpdateComponent?.(model.id, { ...updateModel });
    };

    const onBringToBackAnswerObject = (id: string) => {
        const itemToMove = model.content.answers.find((item) => item.id === id);
        const arrayWithoutItem = model.content.answers.filter((item) => item.id !== id);

        const updatedContent = { ...model.content, answers: itemToMove ? [itemToMove, ...arrayWithoutItem] : arrayWithoutItem };
        const updateModel = { ...model, content: updatedContent };
        setModel({ ...updateModel });
        onUpdateComponent?.(model.id, { ...updateModel });
    };

    const answer = question?.answers?.[0];
    const content = answer?.data ? JSON.parse(answer.data) : undefined;
    const lastSelectedAnswer = content?.mc?.answer && Array.isArray(content.mc.answer) && content.mc.answer.length > 0 ? content.mc.answer[content.mc.answer.length - 1] : '';

    return (
        <Box
            ref={ref}
            id={`mc-component-${model.id}`}
            sx={{
                position: 'absolute',
                left: tPos.x,
                top: tPos.y,
                m: 'inherit',
                p: 'inherit',
                textAlign: 'left',
                width: tSize.width,
                height: tSize.height,
                pointerEvents: 'none',
            }}
            onClick={(e) => {
                if (!preview) {
                    e.stopPropagation();
                }
            }}
        >
            <ResizeBorder
                resizable={false}
                size={tSize}
                pos={tPos}
                preview={preview}
                isSelected={isEditing}
                onResize={(size, pos) => {
                    setTSize(size);
                    setTPos(pos);
                    const updateModel = { ...model, size, pos };
                    debouncedUpdateComponent?.(model.id, { ...updateModel });
                }}
                slideRef={slideRef}
            >
                {!preview && (
                    <ComponentLabel
                        variant='caption'
                        sx={{
                            ...(!isEditing && {
                                opacity: 0, // Hide by default
                                transition: 'opacity 0.2s ease',
                                [`#mc-component-${model.id}:hover &`]: {
                                    opacity: 1, // Show when text-component is hovered
                                },
                            }),
                        }}
                        noWrap
                    >
                        {t('slide.components.mc.title')}
                    </ComponentLabel>
                )}

                {model.content?.answers?.map((answer, index) => {
                    const selectedResult = question ? { isSelected: lastSelectedAnswer == answer.id, isAnswer: answer.settings?.isAnswer } : undefined;

                    return (
                        <SlideComponentMCAnswerObject
                            key={answer.id}
                            init={{ focus: init?.focus }}
                            model={answer}
                            preview={preview}
                            onDeleteObject={onDeleteAnswerObject}
                            onUpdateObject={onUpdateAnswerObject}
                            onCheckObject={onCheckAnswerObject}
                            onUploadImage={onUploadImage}
                            onBringToFrontHandler={onBringToFrontAnswerObject}
                            onBringToBackHandler={onBringToBackAnswerObject}
                            baseSize={model.size}
                            answerIndex={index}
                            answerCount={model.content.answers.length}
                            selectedResult={selectedResult}
                            slideRef={slideRef}
                        />
                    );
                })}

                {!preview && isEditing && (
                    <ContextualBar
                        startButtons={[
                            {
                                icon: <AddBoxOutlinedIcon />,
                                tooltip: t('slide.components.mc.tooltip.addAnswerObject'),
                                onClick: () => {
                                    onCreateAnswerObject({
                                        id: crypto.randomUUID(),
                                        type: 'text',
                                        pos: { x: 0, y: 0 },
                                        size: { width: 0, height: 0 },
                                        settings: {
                                            backgroundColor: '#DADEE7',
                                            isAnswer: false,
                                        },
                                        content: {
                                            text: '',
                                            fontSize: 16,
                                            jyutping: [],
                                            color: '#000000',
                                            typeSettings: {
                                                fontFamily: FONT_FAMILIES[0]?.fontFamily || 'Noto Sans SC',
                                                formatting: [],
                                                alignment: 'left',
                                                isPhonetic: false,
                                                borderRadius: 0,
                                                backgroundColor: 'transparent',
                                            },
                                        },
                                    });
                                },
                            },
                        ]}
                        endButtons={[
                            {
                                icon: <DeleteIcon sx={{ color: (theme) => (theme.vars || theme).palette.error.main }} />,
                                tooltip: t('slide.components.mc.tooltip.remove'),
                                onClick: () => {
                                    onDeleteComponent?.(model.id);
                                },
                            },
                        ]}
                    />
                )}
            </ResizeBorder>
        </Box>
    );
};
